import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Download,
  Loader2,
  RotateCcw,
} from 'lucide-react';
import {
  QrTemplate,
  QrTemplateBusinessInfo,
  QrMode,
  PartialColors,
  SlotOffset,
  resolveSlotDefault,
} from '@/pages/qrTemplates/types';
import {
  canvasToPngDataUrl,
  downloadCanvasAsPng,
  loadQrImageFromCanvas,
  renderTemplateToCanvas,
  sanitizeFileName,
} from '@/pages/qrTemplates/render';

interface Props {
  open: boolean;
  onClose: () => void;
  template: QrTemplate | null;
  qrCanvas: HTMLCanvasElement | null;
  business: QrTemplateBusinessInfo;
  mode: QrMode;
  initialColors?: PartialColors;
}

const NUDGE_STEP: Record<'small' | 'medium' | 'large', number> = {
  small: 10,
  medium: 30,
  large: 80,
};

function nudgeStepForCanvas(maxDim: number): number {
  if (maxDim >= 2000) return NUDGE_STEP.large;
  if (maxDim >= 1000) return NUDGE_STEP.medium;
  return NUDGE_STEP.small;
}

const QrTemplateEditorDialog: React.FC<Props> = ({
  open,
  onClose,
  template,
  qrCanvas,
  business,
  mode,
  initialColors,
}) => {
  const [textOverrides, setTextOverrides] = useState<Record<string, string>>({});
  const [offsetOverrides, setOffsetOverrides] = useState<Record<string, SlotOffset>>({});
  const [colors, setColors] = useState<PartialColors>(initialColors || {});
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const renderTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setTextOverrides({});
    setOffsetOverrides({});
    setColors(initialColors || {});
    setPreviewUrl(null);
  }, [open, template?.id, initialColors]);

  useEffect(() => {
    if (!open || !template || !qrCanvas) return;
    if (renderTimer.current) window.clearTimeout(renderTimer.current);
    renderTimer.current = window.setTimeout(() => {
      (async () => {
        setRendering(true);
        try {
          const qrImage = await loadQrImageFromCanvas(qrCanvas);
          const canvas = renderTemplateToCanvas(template, qrImage, business, mode, {
            colors,
            slotTextOverrides: textOverrides,
            slotOffsetOverrides: offsetOverrides,
          });
          setPreviewUrl(canvasToPngDataUrl(canvas));
        } catch (err) {
          console.error('Preview render failed:', err);
        } finally {
          setRendering(false);
        }
      })();
    }, 150);
    return () => {
      if (renderTimer.current) window.clearTimeout(renderTimer.current);
    };
  }, [open, template, qrCanvas, business, mode, colors, textOverrides, offsetOverrides]);

  const slotDefaults = useMemo(() => {
    if (!template) return {} as Record<string, string>;
    const out: Record<string, string> = {};
    for (const slot of template.slots) {
      out[slot.id] = resolveSlotDefault(slot, mode);
    }
    return out;
  }, [template, mode]);

  if (!template) return null;

  const nudgeStep = nudgeStepForCanvas(Math.max(template.canvasWidth, template.canvasHeight));

  const updateText = (slotId: string, value: string) => {
    setTextOverrides((prev) => ({ ...prev, [slotId]: value }));
  };
  const resetText = (slotId: string) => {
    setTextOverrides((prev) => {
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
  };
  const nudgeOffset = (slotId: string, dx: number, dy: number) => {
    setOffsetOverrides((prev) => {
      const cur = prev[slotId] ?? { x: 0, y: 0 };
      return { ...prev, [slotId]: { x: cur.x + dx, y: cur.y + dy } };
    });
  };
  const resetOffset = (slotId: string) => {
    setOffsetOverrides((prev) => {
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
  };
  const setColor = (key: keyof PartialColors, value: string) => {
    setColors((prev) => ({ ...prev, [key]: value }));
  };
  const resetColor = (key: keyof PartialColors) => {
    setColors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleDownload = async () => {
    if (!qrCanvas || !template) return;
    setDownloading(true);
    try {
      const qrImage = await loadQrImageFromCanvas(qrCanvas);
      const canvas = renderTemplateToCanvas(template, qrImage, business, mode, {
        colors,
        slotTextOverrides: textOverrides,
        slotOffsetOverrides: offsetOverrides,
      });
      const safe = sanitizeFileName(business.name);
      downloadCanvasAsPng(canvas, `${safe}-${template.id}.png`);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const colorSlots: { key: keyof PartialColors; label: string }[] = [
    { key: 'background', label: 'Hintergrund' },
    { key: 'text', label: 'Text' },
    { key: 'accent', label: 'Akzent' },
    { key: 'surface', label: 'Fläche' },
    { key: 'textMuted', label: 'Dezenter Text' },
    { key: 'onAccent', label: 'Text auf Akzent' },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>{template.name} anpassen</DialogTitle>
          <DialogDescription>
            Texte bearbeiten, Positionen feintunen, Farben anpassen. Änderungen aktualisieren die Vorschau live.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 flex-1 overflow-hidden min-h-0">
          <div className="bg-muted rounded-lg flex items-center justify-center p-4 overflow-auto relative min-h-[300px]">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Vorschau"
                className="max-w-full max-h-[72vh] object-contain shadow-lg"
              />
            ) : (
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            )}
            {rendering && previewUrl && (
              <div className="absolute top-2 right-2 bg-card/90 rounded-full p-1.5 shadow">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>

          <div className="overflow-y-auto pr-2 space-y-5">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  Texte
                </Label>
                {Object.keys(textOverrides).length > 0 && (
                  <button
                    type="button"
                    onClick={() => setTextOverrides({})}
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                  >
                    <RotateCcw className="h-3 w-3" />
                    Alle zurücksetzen
                  </button>
                )}
              </div>

              {template.slots.map((slot) => {
                const value = textOverrides[slot.id] ?? slotDefaults[slot.id];
                const modified = textOverrides[slot.id] !== undefined;
                const offset = offsetOverrides[slot.id] ?? { x: 0, y: 0 };
                const offsetModified = offsetOverrides[slot.id] !== undefined && (offset.x !== 0 || offset.y !== 0);
                return (
                  <div key={slot.id} className="border border-border rounded-md p-3 space-y-2 bg-card">
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-xs font-medium text-muted-foreground">{slot.label}</Label>
                      {modified && (
                        <button
                          type="button"
                          onClick={() => resetText(slot.id)}
                          title="Text zurücksetzen"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <RotateCcw className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                    {slot.multiline ? (
                      <Textarea
                        value={value}
                        onChange={(e) => updateText(slot.id, e.target.value)}
                        rows={2}
                        className="text-xs resize-none"
                      />
                    ) : (
                      <Input
                        value={value}
                        onChange={(e) => updateText(slot.id, e.target.value)}
                        className="text-xs h-8"
                      />
                    )}
                    {slot.positionable && (
                      <div className="flex items-center gap-1 pt-1">
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wider flex-1">
                          Position
                          {offsetModified && (
                            <span className="ml-1 font-mono text-muted-foreground normal-case tracking-normal">
                              ({offset.x}, {offset.y})
                            </span>
                          )}
                        </span>
                        <button type="button" onClick={() => nudgeOffset(slot.id, -nudgeStep, 0)} className="p-1 hover:bg-muted rounded" title="Links"><ArrowLeft className="h-3 w-3" /></button>
                        <button type="button" onClick={() => nudgeOffset(slot.id, 0, -nudgeStep)} className="p-1 hover:bg-muted rounded" title="Hoch"><ArrowUp className="h-3 w-3" /></button>
                        <button type="button" onClick={() => nudgeOffset(slot.id, 0, nudgeStep)} className="p-1 hover:bg-muted rounded" title="Runter"><ArrowDown className="h-3 w-3" /></button>
                        <button type="button" onClick={() => nudgeOffset(slot.id, nudgeStep, 0)} className="p-1 hover:bg-muted rounded" title="Rechts"><ArrowRight className="h-3 w-3" /></button>
                        {offsetModified && (
                          <button type="button" onClick={() => resetOffset(slot.id)} className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground" title="Position zurücksetzen">
                            <RotateCcw className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  Farben
                </Label>
                {Object.keys(colors).length > 0 && (
                  <button
                    type="button"
                    onClick={() => setColors({})}
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                  >
                    <RotateCcw className="h-3 w-3" />
                    Alle zurücksetzen
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {colorSlots.map((slot) => {
                  const currentValue = (colors[slot.key] as string | undefined) || template.defaultColors[slot.key];
                  const modified = colors[slot.key] !== undefined;
                  return (
                    <div key={slot.key} className="flex items-center gap-2 border border-border rounded-md p-2 bg-card">
                      <input
                        type="color"
                        value={currentValue}
                        onChange={(e) => setColor(slot.key, e.target.value)}
                        className="h-7 w-7 rounded border border-border cursor-pointer p-0 shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] text-muted-foreground truncate">{slot.label}</div>
                        <div className="text-[9px] text-muted-foreground font-mono uppercase">{currentValue}</div>
                      </div>
                      {modified && (
                        <button
                          type="button"
                          onClick={() => resetColor(slot.key)}
                          className="text-muted-foreground hover:text-foreground shrink-0"
                          title="Zurücksetzen"
                        >
                          <RotateCcw className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 flex flex-col-reverse sm:flex-row sm:justify-between gap-2 border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            onClick={handleDownload}
            disabled={!previewUrl || downloading}
            className="bg-[#1967D2] hover:bg-[#1558b0]"
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Als PNG herunterladen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QrTemplateEditorDialog;
